import { ref } from 'vue'
import { defineStore } from 'pinia'
import { login, getInfo, resetPassword, confirmPassword, logout, refreshAccessToken } from '@/api/auth'
import router from '@/router'
import { useToast } from 'vue-toast-notification'
import dayjs from 'dayjs'
import { Settings, Role } from '@/types'

const toast = useToast({ position: 'top' })

export const useAuthStore = defineStore('auth', () => {
    const accessToken = ref<string>(localStorage.getItem('access') || '')
    const isLoading = ref(false)
    const isAuth = ref<boolean>(!!accessToken.value)
    const isPasswordRecoveryConfirm = ref(false)
    const userInfo = ref<any>()
    const settings = ref<Settings>()
    const permissions = ref<Role | null>(null)
    const isRefreshing = ref(false)
    const refreshTimeOutId = ref()
    const refreshInterval = 12 * 60 * 1000

    function setAccessToken(token: string) {
        accessToken.value = token
        localStorage.setItem('access', token)
    }

    function setRefreshTime(time: string) {
        localStorage.setItem('refreshTime', time)
    }

    function startRefreshTimer() {
        const refreshTime = localStorage.getItem('refreshTime')
        if (!refreshTime) {
            logOut()
            return
        } else {
            const timeFromUpdateRefresh = dayjs().diff(dayjs(refreshTime), 'milliseconds')
            const interval = (refreshInterval - timeFromUpdateRefresh) ? (refreshInterval - timeFromUpdateRefresh) : 0
            // start refresh on timer every refreshTime miliseconds
            refreshTimeOutId.value = setTimeout(async function callRefresh() {
                await refreshToken();
                refreshTimeOutId.value = setTimeout(callRefresh, refreshInterval);
            }, interval)
        }
    }

    async function refreshToken(reload?: boolean) {
        if (isRefreshing.value) return false
        isRefreshing.value = true

        try {
            const res = await refreshAccessToken()
            if (res.data.status === 'ok') {
                setAccessToken(res.data.body.access_token)
                setRefreshTime(dayjs().format('YYYY-MM-DD HH:mm'))
                if (reload) router.go(0)
            }
            isRefreshing.value = false
        } catch (err: any) {
            if (err.status === 401) await logOut()
        }
    }

    async function signIn(data: any) {
        try {
            const res = await login(data)
            setAccessToken(res.data.body.access_token)
            setRefreshTime(dayjs().format('YYYY-MM-DD HH:mm'))
            await getMyInfo()
            if (userInfo.value?.permissions?.can_access_main) {
                isAuth.value = true
                router.push('/')
            } else {
                await logOut()
                toast.error('У вас недостаточно прав для доступа')
            }   
        } catch (err: any) {
            console.log(err.response.data.msg)
            return err
        }
    }

    async function logOut() {
        try {
            clearLS()
            clearTimeout(refreshTimeOutId.value)
            isAuth.value = false
            router.push('/login')
            await logout()
        } catch (err: any) {
            return err
        }
    }

    function clearLS() {
        localStorage.removeItem('access')
        localStorage.removeItem('refreshTime')
        localStorage.removeItem('department')
        localStorage.removeItem('userStat')
        localStorage.removeItem('userStat2')
        localStorage.removeItem('dayStat')
        localStorage.removeItem('dayStat2')
    }

    async function passwordReset(data: any) {
        try {
            const res = await resetPassword(data)
            if (!(res.data.status === 'error')) {
                toast.success(res.data.msg)
                isPasswordRecoveryConfirm.value = true
            }
        } catch (err: any) {
            toast.error(err.response.data.msg)
            return err
        }
    }

    async function passwordConfirm(data: any) {
        try {
            const res = await confirmPassword(data)
            toast.success(res.data.msg)
            isPasswordRecoveryConfirm.value = false
            router.push('/')
        } catch (err: any) {
            toast.error(err.response.data.msg)
            return err
        }
    }

    async function getMyInfo() {
        try {
            const res = await getInfo()
            if(res.data.status === 'ok') {
                userInfo.value = res.data.body
                settings.value = res.data?.body?.settings
                permissions.value = res.data?.body?.permissions
            }
            
        } catch (err: any) {
            return err
        }
    }

    return {
        accessToken,
        isAuth,
        isPasswordRecoveryConfirm,
        userInfo,
        settings,
        isRefreshing,
        permissions,
        isLoading,
        signIn,
        logOut,
        passwordReset,
        passwordConfirm,
        getMyInfo,
        refreshToken,
        startRefreshTimer
    }
})